import React, { useEffect, useRef, useState, memo } from 'react'
import { ItemTypes, pageBackground, VARIABLE_COLOR } from '../../../constants/constants';
import ItemName from './ItemName';
import FormBox from './FormBox';
import { useStore } from '../../../store/store';
import { scrollToItemInVariableEditor } from './FormViewer';

// Mémo pour limiter les re-renders : quand boxVariableSelectedId change dans FormViewer,
// seul le FormItem dont hasVariableSelected change (2 items max) se re-rend.
const FormItem = memo(({
  hasVariableSelected,
  formItem,
  zoom,
  formItems
}) => {
  const ref = useRef(null)
  const areSectionsActivating = useStore(({ areSectionsActivating }) => areSectionsActivating)
  const selectedBoxId = useStore(({ selectedBoxId }) => selectedBoxId)
  const [sectionsInjectionStatus, setSectionsInjectionStatus] = useState(areSectionsActivating ? "loading" : "none")

  useEffect(() => {
    if (areSectionsActivating) {
      setSectionsInjectionStatus("loading")
    }
    if (!areSectionsActivating) {
      setSectionsInjectionStatus((prev) => prev === "loading" && formItem.variablesBoxes.some((v) => v.id === selectedBoxId) ? "done" : "none")
    }
  }, [areSectionsActivating, formItem.variablesBoxes, selectedBoxId])

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (sectionsInjectionStatus === "done") {
        setSectionsInjectionStatus("none")
        if (!entry.isIntersecting)
          scrollToItemInVariableEditor(selectedBoxId, formItems)
      }
    }, {
      root: ref.current.parentNode.parentNode.parentNode.parentNode.parentNode,
      threshold: 0.8,
    })

    observer.observe(ref.current);
    return () => { observer.disconnect(); }
  }, [formItem, selectedBoxId, areSectionsActivating, formItems, sectionsInjectionStatus])

  const getStyle = (variableGroupBox) => {
    const style = {}

    let gridTemplateColumns = "";
    let dispo;
    if (variableGroupBox.disposition) dispo = variableGroupBox.disposition
    else dispo = style.disposition
    for (let i = 0; i < dispo; i++) {
      gridTemplateColumns += "1fr "
    }
    return {
      display: "grid",
      gridTemplateColumns,
    }
  }

  const renderField = (v) =>
    <div key={v.id} style={{
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        padding: "0 16px"
      }}>
        <ItemName
          key={v.id}
          variableBox={v}
          formItem={formItem}
          hasVariableSelected={hasVariableSelected}
          isLast
          zoom={zoom}
        />
      </div>
      <FormBox boxId={v.id} />
    </div>

  const fieldsetBorder = `1px dashed ${hasVariableSelected ? VARIABLE_COLOR : "#b1b1b1"}`
  const legendStyle = {
    zIndex: 0,
    position: 'relative',
    width: "fit-content",
    marginLeft: 16,
    fontSize: 14,
    padding: '0 8px',
    fontWeight: "bold",
    display: 'flex',
    marginBottom: 24
  }

  return (
    <div style={{ backgroundColor: 'white' }} ref={ref}>
      <fieldset data-role={pageBackground} style={{
        border: fieldsetBorder,
        marginBottom: '15px',
        fontStyle: 'italic',
      }}>
        <legend style={legendStyle}>
          {formItem.variableGroupBox ?
            <ItemName
              formItem={formItem}
              variableBox={formItem.variableGroupBox}
              hasVariableSelected={hasVariableSelected}
              isLast
              zoom={zoom}
            /> : formItem.variablesBoxes.map((v, idx) =>
              <ItemName
                key={v.id}
                variableBox={v}
                formItem={formItem}
                hasVariableSelected={hasVariableSelected}
                isLast={idx + 1 === formItem.variablesBoxes.length}
                zoom={zoom}
              />
            )
          }
        </legend>
        {formItem.variableGroupBox ?
          <div data-role={pageBackground} style={{
            ...getStyle(formItem.variableGroupBox)
          }}>
            {formItem.variablesBoxes.filter((vB) => !vB.notUsed)
              .map((v) => v.addressParts ?
                // Adresse découpée dans un groupement : sous-groupe sur toute la
                // largeur, encadré et titré comme un groupement, ses quatre
                // parties dans les colonnes du groupement. Le titre la traite en
                // groupement pour recharger ses parties.
                <fieldset key={v.id} data-role={pageBackground} style={{
                  gridColumn: '1 / -1',
                  margin: '0 16px 16px',
                  minWidth: 0,
                  border: fieldsetBorder,
                }}>
                  <legend style={legendStyle}>
                    <ItemName
                      variableBox={{ ...v, type: ItemTypes.VARIABLE_GROUP }}
                      formItem={{ ...formItem, variablesBoxes: v.addressParts }}
                      hasVariableSelected={hasVariableSelected}
                      isLast
                      zoom={zoom}
                    />
                  </legend>
                  <div data-role={pageBackground} style={getStyle(formItem.variableGroupBox)}>
                    {v.addressParts.map(renderField)}
                  </div>
                </fieldset>
                : renderField(v))
            }
          </div>
          :
          formItem.variablesBoxes && formItem.variablesBoxes.length > 0 ? (
            <FormBox boxId={formItem.variablesBoxes[0].id} />
          ) : null
        }
      </fieldset>
    </div>
  )
})

FormItem.displayName = 'FormItem'

export default FormItem
